/**
 * R20.6: Pagina pubblica firma cliente.
 * Route: /sign/:token — fuori dal router autenticato, nessun redirect al login.
 * Responsive: xs full-screen, canvas ≥ 300×150px su 320px.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import GestureIcon from '@mui/icons-material/Gesture';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import axios from 'axios';

const BASE_URL = '/api/v1/public/signature';

interface TokenInfo {
  status: 'VALID' | 'EXPIRED' | 'ALREADY_USED' | 'NOT_FOUND';
  reportTitle?: string;
  worksiteTitle?: string;
  reportDate?: string;
  operatorName?: string;
  expiresAt?: string;
}

const CANVAS_WIDTH = 600;
const CANVAS_HEIGHT = 160;

export function SignaturePage() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
  const [signerName, setSignerName] = useState('');
  const [isEmpty, setIsEmpty] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);
  const lastPos = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (!token) {
      setTokenInfo({ status: 'NOT_FOUND' });
      setLoading(false);
      return;
    }
    axios
      .get<TokenInfo>(`${BASE_URL}/${token}`)
      .then((r) => setTokenInfo(r.data))
      .catch(() => setTokenInfo({ status: 'NOT_FOUND' }))
      .finally(() => setLoading(false));
  }, [token]);

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = CANVAS_WIDTH * dpr;
    canvas.height = CANVAS_HEIGHT * dpr;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.fillStyle = '#fafafa';
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
  }, []);

  useEffect(() => {
    if (tokenInfo?.status === 'VALID') {
      initCanvas();
    }
  }, [tokenInfo, initCanvas]);

  const getPos = useCallback(
    (e: { clientX: number; clientY: number }, canvas: HTMLCanvasElement) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = CANVAS_WIDTH / rect.width;
      const scaleY = CANVAS_HEIGHT / rect.height;
      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY,
      };
    },
    [],
  );

  const startDraw = useCallback((pos: { x: number; y: number }) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    isDrawing.current = true;
    lastPos.current = pos;
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, 1, 0, Math.PI * 2);
    ctx.fillStyle = '#1a1a2e';
    ctx.fill();
    setIsEmpty(false);
  }, []);

  const draw = useCallback((pos: { x: number; y: number }) => {
    if (!isDrawing.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx || !lastPos.current) return;
    ctx.strokeStyle = '#1a1a2e';
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
  }, []);

  const stopDraw = useCallback(() => {
    isDrawing.current = false;
    lastPos.current = null;
  }, []);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      e.preventDefault();
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.setPointerCapture(e.pointerId);
      startDraw(getPos({ clientX: e.clientX, clientY: e.clientY }, canvas));
    },
    [getPos, startDraw],
  );
  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      draw(getPos({ clientX: e.clientX, clientY: e.clientY }, canvas));
    },
    [getPos, draw],
  );
  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      canvasRef.current?.releasePointerCapture?.(e.pointerId);
      stopDraw();
    },
    [stopDraw],
  );

  const handleClear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#fafafa';
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    setIsEmpty(true);
  };

  const handleSubmit = async () => {
    if (isEmpty) {
      setError('Apponi la firma prima di procedere.');
      return;
    }
    if (!signerName.trim()) {
      setError('Inserisci il tuo nome.');
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas || !token) return;

    setSaving(true);
    setError(null);
    try {
      const base64 = canvas.toDataURL('image/png');
      await axios.post(`${BASE_URL}/${token}`, {
        signerName: signerName.trim(),
        signatureImageBase64: base64,
      });
      setSaved(true);
    } catch (err: unknown) {
      const status = axios.isAxiosError(err) ? err.response?.status : 0;
      if (status === 409) {
        setError('Firma già raccolta per questo rapportino.');
      } else if (status === 410) {
        setError('Questo link di firma è scaduto. Chiedi all\'operatore di generarne uno nuovo.');
      } else {
        setError('Errore durante il salvataggio. Riprova.');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center" bgcolor="#f8fafc">
        <CircularProgress />
      </Box>
    );
  }

  const status = tokenInfo?.status;

  if (saved) {
    return (
      <Box
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bgcolor="#f0fdf4"
        p={2}
      >
        <Paper elevation={3} sx={{ p: { xs: 3, sm: 4 }, maxWidth: 480, width: '100%', textAlign: 'center' }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Firma acquisita con successo
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Grazie. La firma è stata salvata e allegata al rapportino.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (status === 'ALREADY_USED') {
    return (
      <PageShell>
        <Alert severity="info" icon={<CheckCircleOutlineIcon />}>
          <Typography variant="body1" fontWeight={600}>
            Questa firma è già stata raccolta.
          </Typography>
          <Typography variant="body2">Grazie.</Typography>
        </Alert>
      </PageShell>
    );
  }

  if (status === 'EXPIRED' || status === 'NOT_FOUND') {
    return (
      <PageShell>
        <Alert severity="warning">
          <Typography variant="body1" fontWeight={600}>
            Questo link di firma è scaduto.
          </Typography>
          <Typography variant="body2">
            Chiedi all&apos;operatore di generare un nuovo link.
          </Typography>
        </Alert>
      </PageShell>
    );
  }

  return (
    <Box minHeight="100vh" bgcolor="#f8fafc" py={{ xs: 2, sm: 4 }}>
      <Container maxWidth="sm">
        <Stack spacing={3}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <GestureIcon sx={{ color: 'primary.main', fontSize: 32 }} />
            <Typography variant="h6" fontWeight={700} color="primary.main">
              Cantir
            </Typography>
          </Stack>

          <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Firma del rapportino
            </Typography>
            {tokenInfo?.worksiteTitle && (
              <Typography variant="body2" color="text.secondary">
                Commessa: <strong>{tokenInfo.worksiteTitle}</strong>
              </Typography>
            )}
            {tokenInfo?.reportDate && (
              <Typography variant="body2" color="text.secondary">
                Data: <strong>{tokenInfo.reportDate}</strong>
              </Typography>
            )}
            {tokenInfo?.operatorName && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Operatore: <strong>{tokenInfo.operatorName}</strong>
              </Typography>
            )}

            <TextField
              label="Il tuo nome"
              fullWidth
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              placeholder="Nome e cognome"
              size="small"
              sx={{ mb: 2, mt: 1 }}
              inputProps={{ style: { fontSize: 16 } }}
            />

            <Typography variant="body2" color="text.secondary" gutterBottom>
              Firma nell&apos;area sottostante:
            </Typography>

            <Box
              sx={{
                border: '2px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: '#fafafa',
                touchAction: 'none',
                '& canvas': {
                  display: 'block',
                  width: '100%',
                  minHeight: 150,
                  height: { xs: 150, sm: 160 },
                  cursor: 'crosshair',
                  touchAction: 'none',
                },
              }}
            >
              <canvas
                ref={canvasRef}
                width={CANVAS_WIDTH}
                height={CANVAS_HEIGHT}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerLeave={stopDraw}
                onPointerCancel={handlePointerUp}
                style={{
                  display: 'block',
                  width: '100%',
                  minHeight: 150,
                  cursor: 'crosshair',
                  touchAction: 'none',
                }}
              />
            </Box>
            {isEmpty && (
              <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5 }}>
                Disegna la firma con il dito o il mouse
              </Typography>
            )}

            <Stack direction="row" justifyContent="flex-end" mt={1} mb={1}>
              <Button
                size="small"
                startIcon={<ClearIcon />}
                onClick={handleClear}
                disabled={isEmpty || saving}
                color="inherit"
              >
                Cancella
              </Button>
            </Stack>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handleSubmit}
              disabled={saving || isEmpty || !signerName.trim()}
              startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{ minHeight: 48 }}
            >
              {saving ? 'Salvataggio…' : 'Firma e conferma'}
            </Button>
          </Paper>

          <Typography variant="caption" color="text.disabled" textAlign="center">
            Firma operativa/contrattuale — non firma digitale qualificata eIDAS.
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
}

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center" bgcolor="#f8fafc" p={2}>
      <Paper elevation={2} sx={{ p: { xs: 3, sm: 4 }, maxWidth: 480, width: '100%' }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={3}>
          <GestureIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h6" fontWeight={700} color="primary.main">
            Cantir
          </Typography>
        </Stack>
        {children}
      </Paper>
    </Box>
  );
}
